import '../styles/LandingHeroStage.css';
import React, { useState } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';
import { useProjectCollection } from '../lib/projectData';

const PROFILE_FALLBACK = {
  id: 'umang-suthar',
  name: 'Umang Suthar',
  displayName: 'UMANG SUTHAR',
  title: 'FULL STACK DEVELOPER',
  photoAlt: 'Portrait of Umang Suthar',
  photoSrc: '',
};

/* Single sweep shared by the pearl fill and the gold hairline arc so the arc traces the same curve. */
const SWEEP_PATH =
  'M0,300 C 180,120 420,40 760,28 C 1160,14 1560,90 1800,190 L 1800,520 L 0,520 Z';

const ARC_PATH = 'M0,300 C 180,120 420,40 760,28 C 1160,14 1560,90 1800,190';

const CONTOUR_LINES = [
  'M-40,540 C 240,360 520,300 860,330 C 1180,358 1400,470 1540,640',
  'M-40,600 C 260,410 540,346 880,376 C 1200,404 1420,512 1560,690',
  'M-40,660 C 280,462 560,394 900,424 C 1220,452 1440,558 1580,744',
  'M60,260 C 340,120 640,70 980,110 C 1280,146 1460,270 1620,430',
  'M20,200 C 320,60 640,10 1000,48 C 1300,80 1480,190 1660,330',
  'M140,760 C 420,600 700,540 1040,566 C 1340,590 1520,680 1660,840',
  'M980,-60 C 1180,40 1320,180 1380,340',
  'M1060,-60 C 1260,50 1400,190 1460,360',
];

function HeroPortraitImage({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className="hs-aperture-empty">
        <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
          <circle cx="32" cy="24" r="11" fill="none" stroke="currentColor" strokeWidth="1" />
          <path d="M12,58 C14,42 24,36 32,36 C40,36 50,42 52,58" fill="none" stroke="currentColor" strokeWidth="1" />
        </svg>
        <span>Portrait unavailable</span>
      </div>
    );
  }
  return (
    <img
      className="hs-portrait-img"
      src={src}
      alt={alt || 'Portrait of Umang Suthar'}
      onError={() => setFailed(true)}
    />
  );
}

function LandingHeroStage() {
  const { data, loading, error, refresh } = useProjectCollection('profiles');
  const reduceMotion = useReducedMotion();

  const { scrollYProgress } = useScroll();
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const nameY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  const record = Array.isArray(data) && data.length ? data[0] : null;
  const profile = { ...PROFILE_FALLBACK, ...(record || {}) };
  const name = (profile.displayName || profile.name || PROFILE_FALLBACK.displayName).trim();

  const nameWords = name.split(/\s+/);
  const nameLines =
    nameWords.length > 1
      ? [nameWords.slice(0, -1).join(' '), nameWords[nameWords.length - 1]]
      : [name];

  const bgStyle = reduceMotion ? undefined : { transform: bgY };
  const portraitStyle = reduceMotion ? undefined : { transform: portraitY };
  const nameStyle = reduceMotion ? undefined : { transform: nameY };

  return (
    <section className="hs-root" aria-labelledby="hs-hero-name">
      <div className="hs-stage">
        <div className="hs-contour" style={bgStyle} aria-hidden="true">
          <svg viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice">
            <g fill="none" stroke="currentColor" strokeWidth="1" vectorEffect="non-scaling-stroke">
              {CONTOUR_LINES.map((d) => (
                <path key={d} d={d} />
              ))}
            </g>
          </svg>
        </div>

        <div className="hs-midground" style={portraitStyle}>
          {loading && !record ? (
            <div className="hs-aperture">
              <div className="hs-aperture-empty" role="status">
                <span>Loading portrait</span>
              </div>
            </div>
          ) : (
            <div className="hs-aperture">
              <HeroPortraitImage src={profile.photoSrc} alt={profile.photoAlt} />
              <div className="hs-duotone" aria-hidden="true" />
              <div className="hs-duotone-shade" aria-hidden="true" />
            </div>
          )}
        </div>

        <div className="hs-sweep" aria-hidden="true">
          <svg viewBox="0 0 1800 520" preserveAspectRatio="none">
            <path className="hs-sweep-fill" d={SWEEP_PATH} />
            <path className="hs-sweep-edge" d={ARC_PATH} vectorEffect="non-scaling-stroke" />
          </svg>
        </div>

        <div className="hs-arc-wrap" aria-hidden="true">
          <svg viewBox="0 0 1800 520" preserveAspectRatio="none">
            <motion.path
              className="hs-arc"
              d={ARC_PATH}
              vectorEffect="non-scaling-stroke"
              initial={reduceMotion ? { pathLength: 1 } : { pathLength: 0, opacity: 0 }}
              animate={reduceMotion ? { pathLength: 1 } : { pathLength: 1, opacity: 1 }}
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : { pathLength: { duration: 20, ease: [0.25, 0.6, 0.2, 1] }, opacity: { duration: 1.2, ease: 'easeOut' } }
              }
            />
          </svg>
        </div>

        <div className="hs-content" data-content-rail="primary">
          <div className="hs-name-block" style={nameStyle}>
            <p className="hs-eyebrow">Portfolio — 01</p>
            <h1 className="hs-name" id="hs-hero-name">
              {nameLines.map((line) => (
                <span className="hs-name-line" key={line}>
                  {line}
                </span>
              ))}
            </h1>
            <div className="hs-title-row">
              <p className="hs-title">{profile.title || PROFILE_FALLBACK.title}</p>
            </div>
          </div>

          <div className="hs-anchor-rail">
            <div className="hs-anchor-inner">
              <a className="hs-work-link" href="/portfolio">
                <span>View work</span>
                <span className="hs-work-arrow" aria-hidden="true">
                  →
                </span>
              </a>
              <a className="hs-work-link hs-contact-link" href="/contact">
                <span>Get in touch</span>
                <span className="hs-work-arrow" aria-hidden="true">
                  →
                </span>
              </a>
              {error ? (
                <p className="hs-status hs-status--error" role="alert">
                  Profile details could not be loaded.
                  <button type="button" onClick={() => refresh && refresh()}>
                    Retry
                  </button>
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LandingHeroStage;